import { thumb } from "../lib/site";
import { useLocation, Link, Navigate } from "react-router-dom";
import { ArrowRight, MessageCircle, Check } from "lucide-react";
import SEO from "../components/SEO";
import { serviceSchema, faqSchema } from "../data/schema";
import { Reveal, Stagger, Item, Chapter, MaskedLine } from "../components/Reveal";
import { openLeadPopup } from "../components/Navbar";
import { MARKETS } from "../data/markets";
import { PROJECTS } from "../data/content";
import { PRICING, COUNTRY_CODE } from "../data/pricing";
import { waLink, CONTEXT_MESSAGES } from "../lib/whatsapp";
import { track } from "../lib/analytics";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

// What each service includes, per the service pages and FAQs.
const INCLUDES = {
  "Website Development": [
    "Premium custom design — no templates",
    "Mobile-first, fast-loading build",
    "SEO foundation included",
    "WhatsApp & lead capture integration",
    "Conversion-focused structure",
    "Optional monthly growth plan",
  ],
  "Shopify Development": [
    "Custom Shopify store design",
    "Conversion-focused product pages",
    "Payments, shipping and taxes configured",
    "Speed optimisation",
    "Store SEO foundation",
    "Optional monthly growth plan",
  ],
  "SEO Services": [
    "Technical SEO audit and fixes",
    "Keyword research for buyer searches",
    "Service and location pages",
    "Google Business Profile optimisation",
    "Content that answers customer questions",
    "Monthly progress on rankings and enquiries",
  ],
  "AI Chatbot Development": [
    "Chatbot trained on your services and FAQs",
    "Website and WhatsApp deployment",
    "Lead capture to WhatsApp, email or CRM",
    "Hand-off to your team with full context",
    "Works with WordPress, Shopify or custom sites",
    "Tuning from real conversations",
  ],
  "AI Business Automation": [
    "Instant replies to new enquiries",
    "Automatic follow-ups",
    "Lead qualification and routing",
    "CRM and WhatsApp workflows",
    "Review requests after delivery",
    "Built and maintained for you",
  ],
};

export default function MarketPage() {
  const { pathname } = useLocation();
  const market = MARKETS.find((m) => m.slug === pathname.replace(/\/+$/, "").slice(1));
  if (!market) return <Navigate to="/" replace />;

  const price = PRICING[COUNTRY_CODE[market.country]];
  const short = market.country === "United States" ? "USA" : market.country === "United Arab Emirates" ? "UAE" : "India";
  const sameCountry = MARKETS.filter((m) => m.country === market.country && m.slug !== market.slug);
  const sameService = MARKETS.filter((m) => m.service === market.service && m.slug !== market.slug);
  const waKey = market.service.includes("Shopify") ? "shopify" : market.service.includes("AI") ? "ai" : market.service.includes("SEO") ? "seo" : "new";

  return (
    <main className="pt-36 lg:pt-44">
      <SEO
        title={`${market.heroTitle} | Grotech Web`}
        description={market.metaDesc}
        path={`/${market.slug}`}
        schema={[
          serviceSchema({
            name: `${market.service} — ${market.country}`,
            description: market.metaDesc,
            path: `/${market.slug}`,
            serviceType: market.service,
            areaServed: { "@type": "Country", name: market.country },
          }),
          faqSchema(market.faqs),
        ]}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: market.heroTitle, path: `/${market.slug}` }]}
      />

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16" data-testid="market-hero">
        <nav className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link>
          <span className="mx-2 text-ember">/</span>
          <span className="text-white/70">{market.service} · {market.country}</span>
        </nav>
        <h1 className="mt-8 max-w-4xl font-display text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
          <MaskedLine>{market.heroTitle}</MaskedLine>
        </h1>
        <Reveal hero delay={0.25}>
          {market.intro.map((p, i) => (
            <p key={i} className="mt-5 max-w-3xl text-base leading-relaxed text-white/60 sm:text-lg">
              {p}
            </p>
          ))}
          <div className="mt-9 flex flex-wrap gap-4">
            <button
              data-testid="market-primary-cta"
              onClick={() => { track("cta_click", { location: "market", market: market.slug }); openLeadPopup(); }}
              className="group flex items-center gap-2 rounded-full bg-ember px-7 py-4 font-display text-sm font-bold uppercase tracking-wider transition-colors hover:bg-ember-hover"
            >
              Get My Free Growth Review
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href={waLink(CONTEXT_MESSAGES[waKey])}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="market-whatsapp-cta"
              onClick={() => track("whatsapp_click", { location: "market_page", market: market.slug })}
              className="flex items-center gap-2 rounded-full border border-[#25D366]/50 px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-[#25D366] transition-colors hover:bg-[#25D366]/10"
            >
              <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
            </a>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-24">
        <Chapter index="01" label="What's Included" />
        <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {(INCLUDES[market.service] || INCLUDES["Website Development"]).map((f) => (
            <Item key={f} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-ink p-6">
              <Check className="mt-0.5 h-5 w-5 shrink-0 text-ember" />
              <p className="text-sm leading-relaxed text-white/70">{f}</p>
            </Item>
          ))}
        </Stagger>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-8 lg:px-16 lg:pb-24" data-testid="market-pricing">
        <Chapter index="02" label={`Pricing for ${short}`} />
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          <div className="rounded-3xl border border-ember/40 bg-ink p-8">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember">Website build</p>
            <p className="mt-4 font-display text-4xl font-black">from {price.website}</p>
            <p className="mt-3 text-sm leading-relaxed text-white/60">
              One-time cost for a premium, SEO-ready {market.service.toLowerCase()} project. Exact scope and price agreed in writing first.
            </p>
          </div>
          <div className="rounded-3xl border border-white/10 bg-ink p-8">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/50">Monthly growth plan (optional)</p>
            <p className="mt-4 font-display text-4xl font-black">from {price.growth}<span className="text-lg font-bold text-white/50">/month</span></p>
            <p className="mt-3 text-sm leading-relaxed text-white/60">
              SEO, new pages, content and conversion improvements every month. Cancel any time.
            </p>
          </div>
        </div>
        <p className="mt-5 text-sm text-white/50">
          {price.note}{" "}
          <Link to="/pricing/" className="font-semibold text-ember underline-offset-2 hover:underline">See full pricing</Link>
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-8 lg:px-16 lg:pb-24" data-testid="market-proof">
        <Chapter index="03" label="Recent websites we built" />
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PROJECTS.slice(0, 3).map((p) => (
            <Link key={p.slug} to={`/projects/${p.slug}/`} className="group overflow-hidden rounded-3xl border border-white/10 bg-ink transition-colors hover:border-ember/40">
              <img src={thumb(p.image)} alt={`${p.name} website by Grotech Web`} width="800" height="500" loading="lazy" decoding="async" className="aspect-[16/10] w-full object-cover" />
              <div className="p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ember">{p.industry}</p>
                <p className="mt-2 font-display text-lg font-bold group-hover:text-ember">{p.name}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="border-t border-line bg-ink py-20 lg:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-8">
          <Chapter index="04" label={`Questions from ${market.country}`} />
          <div className="mt-8">
            <Accordion type="single" collapsible data-testid="market-faq">
              {market.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger className="text-left font-display text-base font-semibold text-white hover:text-ember hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-white/55">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <Reveal>
            <div className="mt-12 rounded-3xl bg-bone p-8 text-obsidian sm:p-10">
              <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                Ready to grow your {short === "India" ? "Indian" : short} business online?
              </h2>
              <p className="mt-3 max-w-md text-sm text-obsidian/60">
                Get a free growth review — we'll show you exactly where your online presence is
                losing enquiries and what to fix first.
              </p>
              <button
                data-testid="market-bottom-cta"
                onClick={openLeadPopup}
                className="mt-7 rounded-full bg-ember px-7 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-ember-hover"
              >
                Get My Free Growth Review
              </button>
            </div>
          </Reveal>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16" data-testid="market-related">
        <div className="grid gap-10 md:grid-cols-2">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">More services in {short}</p>
            <ul className="mt-4 space-y-2">
              {sameCountry.map((m) => (
                <li key={m.slug}><Link to={`/${m.slug}/`} className="text-sm text-white/70 underline-offset-2 hover:text-ember hover:underline">{m.heroTitle}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">{market.service} in other countries</p>
            <ul className="mt-4 space-y-2">
              {sameService.map((m) => (
                <li key={m.slug}><Link to={`/${m.slug}/`} className="text-sm text-white/70 underline-offset-2 hover:text-ember hover:underline">{m.heroTitle}</Link></li>
              ))}
              <li><Link to="/services/" className="text-sm text-white/70 underline-offset-2 hover:text-ember hover:underline">All services</Link></li>
            </ul>
          </div>
        </div>
      </section>
    </main>
  );
}
